import React, { useState } from 'react'
import { createUseStyles } from 'react-jss'
import { IkonFilliste } from '../../../../assets/icons'
import { CaseDetail, File } from '../../../../models/CaseDetail'
import { formatDate } from '../../../../utils/format'
import { Typography } from '../../../ui/Typography/Typography'
import { colors } from '../../../../theme/palette'
import { Button } from '../../../ui/Button/Button'
import { Loader } from '../../../ui/Loaders/Spinner'
import { L } from '../../../providers/Lokalise'
import {
    useFileDownload,
    useErrorDialog,
} from '../../../../hooks/useFileDownload'
import { ErrorDisplay } from '../Contact/ContactDialog'

const useStyles = createUseStyles({
    tooltip: {
        position: 'absolute',
        backgroundColor: colors.gray800,
        color: colors.white,
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: '12px',
        whiteSpace: 'nowrap',
        zIndex: 1000,
        opacity: 0,
        visibility: 'hidden',
        transition: 'opacity 0.2s, visibility 0.2s',
        bottom: '100%',
        left: '50%',
        transform: 'translateX(-50%)',
        marginBottom: '5px',
        '&.visible': {
            opacity: 1,
            visibility: 'visible',
        },
    },
    buttonWrapper: {
        position: 'relative',
        width: '100%',
    },
})

const CaseFilesRowCard: React.FC<{
    file: File
    onUpdated: () => void
    caseDetails: CaseDetail
}> = ({ file, onUpdated, caseDetails }) => {
    const [showTooltip, setShowTooltip] = useState(false)
    const classes = useStyles()
    const { isLoading, openFile } = useFileDownload()
    const { ErrorDialogComponent } = useErrorDialog()

    const handleOpenFile = async (): Promise<void> => {
        await openFile({
            caseNumber: file.caseNumber,
            fullName: file.fullName,
            extension: file.extension,
        })
    }

    return (
        <div
            className={classes.buttonWrapper}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
        >
            <Button
                type="action"
                onClick={handleOpenFile}
                style={{
                    width: '100%',
                    textAlign: 'left',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        gap: '.5rem',
                        alignItems: 'center',
                        border: `1px solid ${colors.borderColor}`,
                        padding: '0.5rem',
                        borderRadius: '0.5rem',
                    }}
                >
                    {isLoading && (
                        <div
                            style={{
                                position: 'absolute',
                                top: '0',
                                left: '0',
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 1,
                                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                            }}
                        >
                            <Loader />
                        </div>
                    )}
                    <div style={{ width: '1.5rem', flexShrink: 0 }}>
                        <IkonFilliste fill={colors.green500} />
                    </div>
                    <div
                        style={{
                            flexGrow: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                        }}
                    >
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr auto',
                                gap: '0.5rem',
                            }}
                        >
                            <div
                                style={{
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap',
                                    textOverflow: 'ellipsis',
                                    width: '95%',
                                    color: colors.black,
                                    fontWeight: 'bold',
                                    lineHeight: '1.2',
                                }}
                            >
                                {file.title}
                            </div>
                            <div>
                                <Typography
                                    tag="p"
                                    size="XXS"
                                    weight="bold"
                                    style={{
                                        display: 'flex',
                                        gap: '0.5rem',
                                        justifyContent: 'space-between',
                                        marginBottom: '0rem',
                                        paddingBottom: '0rem',
                                        lineHeight: '1',
                                    }}
                                >
                                    <span
                                        style={{
                                            maxWidth: '80px',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            display: 'inline-block',
                                        }}
                                    >
                                        {file.extension}
                                    </span>
                                </Typography>
                            </div>
                        </div>
                        <Typography
                            tag="p"
                            size="XXS"
                            style={{
                                display: 'flex',
                                gap: '0.5rem',
                                justifyContent: 'space-between',
                                color: colors.gray800,
                                marginTop: '0.2rem',
                                lineHeight: '1',
                            }}
                        >
                            <span>{`${L(
                                'LØN_SAGS_FIL_KORT_OPRETTET_TID_LABEL'
                            )} ${formatDate(new Date(file.changeTime))}`}</span>
                            {caseDetails.caseNumber !== file.caseNumber && (
                                <span>{file.caseNumber}</span>
                            )}
                        </Typography>
                    </div>
                </div>
            </Button>
            <div
                className={`${classes.tooltip} ${showTooltip ? 'visible' : ''}`}
            >
                {file.fullName}
            </div>
            <ErrorDialogComponent />
        </div>
    )
}

export { CaseFilesRowCard }
